// Guest-facing in-room service page — reached via QR code posted in each room.
// Three flows: Order F&B/amenities, Request housekeeping, Rate the stay.
// Localised to the guest's browser language (7 locales) and auto-attaches
// orders to the in-house folio so items land on the check-out invoice.
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Utensils, Sparkles, Star, CheckCircle2, Loader2, Plus, Minus, ShoppingBag,
  Bed, ChevronRight, ArrowLeft, MessageSquare, Receipt, Camera,
} from 'lucide-react';
import {
  detectInRoomLang, t as tt, menuName, sectionName,
  requestLabel, surveyLabel,
} from './inroom_i18n';

import { OrderTracker, RecentOrders, rememberOrder } from '../components/inroom/OrderTracker';
import { RequestTracker, RecentRequests, rememberRequest } from '../components/inroom/RequestTracker';

const API = process.env.REACT_APP_BACKEND_URL || '';

const eur = (n) => `€${(Number(n || 0)).toFixed(2)}`;

const StarRow = ({ value, onChange, testId }) => {
  const stars = [1, 2, 3, 4, 5];
  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      {stars.map((s) => (
        <button
          type="button"
          key={s}
          onClick={() => onChange(s)}
          aria-label={`${s} stars`}
          className={`p-1 transition-transform ${value >= s ? 'text-amber-400' : 'text-slate-300'} hover:scale-110`}
        >
          <Star className={`w-7 h-7 ${value >= s ? 'fill-amber-400' : ''}`} />
        </button>
      ))}
      {value > 0 && <span className="ml-2 text-sm text-slate-500 font-medium">{value}/5</span>}
    </div>
  );
};

const InRoom = () => {
  const { roomId } = useParams();
  // `?unit=302` — bakes the physical room number into every order/request/survey
  // so kitchen + housekeeping know exactly which unit to deliver to.
  const unit = React.useMemo(() => {
    if (typeof window === 'undefined') return '';
    return (new URLSearchParams(window.location.search).get('unit') || '').trim().slice(0, 12);
  }, []);
  const unitQuery = unit ? `?unit=${encodeURIComponent(unit)}` : '';
  const [lang] = useState(detectInRoomLang);
  const T = (key) => tt(lang, key);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('menu'); // menu | request | survey
  const [cart, setCart] = useState({}); // sku → qty
  const [orderNote, setOrderNote] = useState('');
  const [guestName, setGuestName] = useState('');
  const [placing, setPlacing] = useState(false);
  const [orderDone, setOrderDone] = useState(null);

  const [reqType, setReqType] = useState('');
  const [reqNote, setReqNote] = useState('');
  const [reqSending, setReqSending] = useState(false);
  const [reqDone, setReqDone] = useState(null);

  const [ratings, setRatings] = useState({});
  const [surveyComment, setSurveyComment] = useState('');
  const [surveySending, setSurveySending] = useState(false);
  const [surveyDone, setSurveyDone] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/inroom/rooms/${roomId}?lang=${lang}`);
        if (!r.ok) throw new Error((await r.json()).detail || 'Room not found');
        const j = await r.json();
        if (live) setData(j);
      } catch (e) {
        if (live) setErr(e.message);
      }
    })();
    return () => { live = false; };
  }, [roomId]);

  const menuIndex = useMemo(() => {
    const idx = {};
    if (data) Object.values(data.menu).forEach((arr) => arr.forEach((it) => { idx[it.sku] = it; }));
    return idx;
  }, [data]);

  const total = useMemo(
    () => Object.entries(cart).reduce((sum, [sku, q]) => sum + (menuIndex[sku]?.price || 0) * q, 0),
    [cart, menuIndex]
  );
  const cartCount = Object.values(cart).reduce((a, b) => a + b, 0);

  const bump = (sku, delta) => {
    setCart((prev) => {
      const q = (prev[sku] || 0) + delta;
      const next = { ...prev };
      if (q <= 0) delete next[sku];
      else next[sku] = Math.min(q, 20);
      return next;
    });
  };

  const submitOrder = async () => {
    setPlacing(true);
    try {
      const items = Object.entries(cart).map(([sku, qty]) => ({ sku, qty }));
      const r = await fetch(`${API}/api/inroom/${roomId}/order${unitQuery}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, note: orderNote, guest_name: guestName, lang }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Could not place order');
      setOrderDone({ ...j.order, attached_to_folio: j.attached_to_folio });
      rememberOrder(j.order.id);
    } catch (e) { alert(e.message); }
    finally { setPlacing(false); }
  };

  const submitRequest = async () => {
    if (!reqType) return;
    setReqSending(true);
    try {
      const r = await fetch(`${API}/api/inroom/${roomId}/request${unitQuery}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: reqType, note: reqNote, guest_name: guestName, lang }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Could not send request');
      setReqDone(j.request);
      rememberRequest(j.request.id);
    } catch (e) { alert(e.message); }
    finally { setReqSending(false); }
  };

  const submitSurvey = async () => {
    if (Object.keys(ratings).length === 0) return;
    setSurveySending(true);
    try {
      const r = await fetch(`${API}/api/inroom/${roomId}/survey${unitQuery}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ratings, comment: surveyComment, guest_name: guestName, lang }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Could not submit');
      setSurveyDone(j.survey);
    } catch (e) { alert(e.message); }
    finally { setSurveySending(false); }
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
        <div className="max-w-md text-center">
          <p className="text-4xl mb-3">🛎</p>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{T('notFound')}</h1>
          <p className="text-slate-600">{err}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const { room, menu, housekeeping_types, survey_categories, active_folio } = data;
  const orderingEnabled = data.ordering_enabled !== false;

  // Hoist all inline `.map()` iterations OUT of JSX to keep the visual-edits
  // Babel plugin from stack-overflowing on deeply nested member expressions.
  const tabDefs = [
    { k: 'menu', label: T('tabOrder'), icon: Utensils },
    { k: 'request', label: T('tabRequest'), icon: Sparkles },
    { k: 'survey', label: T('tabSurvey'), icon: Star },
  ].filter((d) => d.k !== 'menu' || orderingEnabled);
  const tabBtns = tabDefs.map(({ k, label, icon: Icon }) => (
    <button
      key={k}
      onClick={() => setTab(k)}
      className={`flex-1 py-4 flex flex-col items-center gap-1 transition-colors ${
        tab === k ? 'bg-orange-500 text-white' : 'text-slate-700 hover:bg-orange-50'
      }`}
      data-testid={`inroom-tab-${k}`}
    >
      <Icon className="w-5 h-5" />
      <span className="text-xs font-semibold">{label}</span>
    </button>
  ));

  const menuSections = Object.entries(menu).map(([section, itemsArr]) => {
    const itemRows = itemsArr.map((it) => {
      const q = cart[it.sku] || 0;
      const localName = menuName(lang, it.sku, it.name);
      return (
        <div key={it.sku} className="flex items-center px-4 py-3">
          {it.image_url && <img src={`${API}${it.image_url}`} alt="" loading="lazy" className="w-14 h-14 rounded-xl object-cover mr-3 flex-shrink-0 bg-slate-100" data-testid={`inroom-photo-${it.sku}`} />}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-900 text-sm">{localName}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {it.price > 0 ? eur(it.price) : T('complimentary')}
            </p>
          </div>
          {q === 0 ? (
            <button
              onClick={() => bump(it.sku, 1)}
              className="px-3 py-1.5 rounded-full bg-orange-500 text-white text-xs font-semibold hover:bg-orange-600 flex items-center gap-1"
              data-testid={`inroom-add-${it.sku}`}
            >
              <Plus className="w-3.5 h-3.5" /> {T('add')}
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={() => bump(it.sku, -1)} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-6 text-center font-bold" data-testid={`inroom-qty-${it.sku}`}>{q}</span>
              <button onClick={() => bump(it.sku, 1)} className="w-8 h-8 rounded-full bg-orange-500 text-white hover:bg-orange-600 flex items-center justify-center">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      );
    });
    return (
      <section key={section} className="mb-5">
        <h2 className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2 px-1">{sectionName(lang, section)}</h2>
        <div className="bg-white rounded-2xl border border-slate-200 divide-y">{itemRows}</div>
      </section>
    );
  });

  const requestRows = housekeeping_types.map((rt) => (
    <button
      key={rt}
      onClick={() => setReqType(rt)}
      className={`w-full flex items-center px-4 py-3 text-left transition-colors ${
        reqType === rt ? 'bg-orange-50' : 'hover:bg-slate-50'
      }`}
      data-testid={`inroom-req-${rt}`}
    >
      <Bed className={`w-5 h-5 mr-3 ${reqType === rt ? 'text-orange-500' : 'text-slate-400'}`} />
      <span className={`flex-1 text-sm ${reqType === rt ? 'font-semibold text-orange-700' : 'text-slate-800'}`}>
        {requestLabel(lang, rt)}
      </span>
      {reqType === rt && <CheckCircle2 className="w-5 h-5 text-orange-500" />}
    </button>
  ));

  const surveyRows = survey_categories.map((cat) => (
    <div key={cat} className="px-4 py-3">
      <p className="text-sm font-semibold text-slate-800 mb-1">{surveyLabel(lang, cat)}</p>
      <StarRow
        value={ratings[cat] || 0}
        onChange={(v) => setRatings((r) => ({ ...r, [cat]: v }))}
        testId={`inroom-survey-${cat}`}
      />
    </div>
  ));

  const chargeBanner = active_folio ? (
    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4 flex items-start gap-3" data-testid="inroom-charge-banner">
      <Receipt className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-emerald-900">
          {T('chargeToRoom')(active_folio.guest_first_name)}
        </p>
        <p className="text-xs text-emerald-700 mt-0.5">{T('chargeToRoomHint')}</p>
      </div>
    </div>
  ) : null;

  const photoNudge = active_folio ? <PhotoNudge roomId={roomId} lang={lang} /> : null;

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="inroom-page">
      {/* Hero */}
      <div className="relative h-56 sm:h-64 bg-slate-800 overflow-hidden">
        {room.hero_image && (
          <img
            src={`${API}${room.hero_image}`}
            alt={room.name}
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-6 text-white max-w-3xl mx-auto">
          <p className="text-xs uppercase tracking-widest opacity-90">{T('kicker')}</p>
          <div className="flex items-center gap-3 mt-1">
            <h1 className="text-2xl sm:text-3xl font-bold" data-testid="inroom-room-name">{room.name}</h1>
            {unit && (
              <span
                className="bg-white/90 text-slate-900 text-xs font-bold px-2 py-0.5 rounded-full"
                data-testid="inroom-unit-badge"
              >Room {unit}</span>
            )}
          </div>
          <p className="text-sm opacity-80 mt-1">{T('greeting')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-3xl mx-auto px-4 -mt-6 relative z-10">
        <div className="bg-white rounded-2xl shadow-lg flex overflow-hidden" data-testid="inroom-tabs">{tabBtns}</div>
      </div>

      <div className="max-w-3xl mx-auto px-4 mt-6">
        {chargeBanner}
        {photoNudge}

        <input
          type="text"
          value={guestName}
          onChange={(e) => setGuestName(e.target.value)}
          placeholder={T('yourName')}
          className="w-full rounded-lg border border-slate-200 px-4 py-2 mb-4 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
          data-testid="inroom-guest-name"
        />

        {/* ORDER MENU */}
        {tab === 'menu' && !orderDone && (
          <div data-testid="inroom-menu">
            <RecentOrders lang={lang} />
            {menuSections}
            {cartCount > 0 && (
              <>
                <textarea
                  value={orderNote}
                  onChange={(e) => setOrderNote(e.target.value)}
                  placeholder={T('orderNote')}
                  className="w-full rounded-lg border border-slate-200 px-4 py-2 mb-3 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
                  rows={2}
                  data-testid="inroom-order-note"
                />
                <div className="fixed bottom-4 left-4 right-4 max-w-3xl mx-auto z-20">
                  <button
                    onClick={submitOrder}
                    disabled={placing}
                    className="w-full py-4 rounded-2xl bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-2xl flex items-center justify-center gap-3 disabled:opacity-60"
                    data-testid="inroom-submit-order"
                  >
                    {placing ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShoppingBag className="w-5 h-5" />}
                    {T('placeOrder')} · {cartCount} {cartCount > 1 ? T('items') : T('item')} · {eur(total)}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {tab === 'menu' && orderDone && (
          <Success
            title={T('orderSuccess')}
            body={T('orderSuccessBody')(orderDone.items.length, eur(orderDone.total_eur))}
            footer={
              <div className="space-y-3">
                <OrderTracker orderId={orderDone.id} lang={lang} />
                {orderDone.attached_to_folio ? (
                  <p className="text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-lg px-3 py-2 inline-flex items-center gap-2" data-testid="inroom-charged-notice">
                    <Receipt className="w-4 h-4" /> {T('chargedNotice')(orderDone.attached_to_folio.guest_name?.split(' ')[0] || 'you')}
                  </p>
                ) : null}
              </div>
            }
            onAgain={() => { setOrderDone(null); setCart({}); setOrderNote(''); }}
            testId="inroom-order-success"
            sendAnother={T('sendAnother')}
          />
        )}

        {/* HOUSEKEEPING REQUEST */}
        {tab === 'request' && !reqDone && (
          <div data-testid="inroom-request">
            <RecentRequests lang={lang} />
            <p className="text-sm text-slate-600 mb-3 px-1">{T('reqIntro')}</p>
            <div className="bg-white rounded-2xl border border-slate-200 divide-y overflow-hidden">{requestRows}</div>
            <textarea
              value={reqNote}
              onChange={(e) => setReqNote(e.target.value)}
              placeholder={T('reqNote')}
              className="w-full mt-3 rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
              rows={2}
              data-testid="inroom-req-note"
            />
            <button
              onClick={submitRequest}
              disabled={!reqType || reqSending}
              className="w-full mt-3 py-4 rounded-2xl bg-orange-500 hover:bg-orange-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="inroom-submit-req"
            >
              {reqSending ? <Loader2 className="w-5 h-5 animate-spin" /> : <ChevronRight className="w-5 h-5" />}
              {T('sendRequest')}
            </button>
          </div>
        )}
        {tab === 'request' && reqDone && (
          <div>
            <Success
              title={T('reqSuccess')}
              body={T('reqSuccessBody')(requestLabel(lang, reqDone.type))}
              onAgain={() => { setReqDone(null); setReqType(''); setReqNote(''); }}
              testId="inroom-req-success"
              sendAnother={T('sendAnother')}
            />
            <div className="mt-3"><RequestTracker requestId={reqDone.id} lang={lang} /></div>
          </div>
        )}

        {/* SURVEY */}
        {tab === 'survey' && !surveyDone && (
          <div data-testid="inroom-survey">
            <p className="text-sm text-slate-600 mb-3 px-1">{T('surveyIntro')}</p>
            <div className="bg-white rounded-2xl border border-slate-200 divide-y">{surveyRows}</div>
            <textarea
              value={surveyComment}
              onChange={(e) => setSurveyComment(e.target.value)}
              placeholder={T('surveyComment')}
              className="w-full mt-3 rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
              rows={3}
              data-testid="inroom-survey-comment"
            />
            <button
              onClick={submitSurvey}
              disabled={Object.keys(ratings).length === 0 || surveySending}
              className="w-full mt-3 py-4 rounded-2xl bg-orange-500 hover:bg-orange-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="inroom-submit-survey"
            >
              {surveySending ? <Loader2 className="w-5 h-5 animate-spin" /> : <MessageSquare className="w-5 h-5" />}
              {T('submitReview')}
            </button>
          </div>
        )}
        {tab === 'survey' && surveyDone && (
          <Success
            title={T('surveyThanks')(surveyDone.overall)}
            body={T('surveySuccessBody')}
            onAgain={() => { setSurveyDone(null); setRatings({}); setSurveyComment(''); }}
            testId="inroom-survey-success"
            sendAnother={T('sendAnother')}
          />
        )}
      </div>
    </div>
  );
};

const PhotoNudge = ({ roomId, lang }) => {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    let live = true;
    fetch(`${API}/api/inroom/${roomId}/photo-link?lang=${lang}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (live && j) setInfo(j); })
      .catch(() => {});
    return () => { live = false; };
  }, [roomId, lang]);
  if (!info) return null;
  return (
    <a
      href={info.url}
      className="block bg-sky-50 border border-sky-200 rounded-xl p-3 mb-4 hover:bg-sky-100 transition-colors"
      data-testid="inroom-photo-nudge"
    >
      <div className="flex items-start gap-3">
        <Camera className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-sky-900" data-testid="inroom-photo-nudge-title">{info.title}</p>
          <p className="text-xs text-sky-700 mt-0.5">{info.lead}</p>
          <span className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-sky-700" data-testid="inroom-photo-nudge-cta">
            {info.cta} <ChevronRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </a>
  );
};

const Success = ({ title, body, onAgain, testId, sendAnother, footer }) => (
  <div className="bg-white rounded-2xl border border-green-200 p-8 text-center" data-testid={testId}>
    <div className="w-16 h-16 mx-auto rounded-full bg-green-50 flex items-center justify-center mb-3">
      <CheckCircle2 className="w-10 h-10 text-green-500" />
    </div>
    <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
    <p className="text-sm text-slate-600 mb-4">{body}</p>
    {footer ? <div className="mb-4">{footer}</div> : null}
    <button
      onClick={onAgain}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-sm"
    >
      <ArrowLeft className="w-4 h-4" /> {sendAnother || 'Send another'}
    </button>
  </div>
);

export default InRoom;
